import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import { useNewClientContext } from 'Context/NewClientContext';
import { STEP_LINK_QUERY_PARAM_NAME } from 'Views/Onboarding/Pages/Survey/SurveyContext';
import history from 'State/history';
import { clientPaths } from 'Consts/Strings';
import useComponentDidMount from 'Hooks/useComponentDidMount';

const DashboardContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const CLIENT_DASHBOARD_REFRESH_STARTED = 'client-refresh-started';
export const CLIENT_DASHBOARD_REFRESH_SUCCEEDED = 'client-refresh-succeeded';
export const CLIENT_DASHBOARD_REFRESH_FAILED = 'client-refresh-failed';

// Initialize State
const init = () => {
  // IMPORTANT: Please be cautious of adding additional state here.
  // Is there a need for it to be loaded concurrently with the client?
  // If not, it may make sense in a separate context.
  return {
    isInitializing: true,
    initializingError: null,
    clientDashboardCollection: null,
    isClientDashboardRefreshing: false,
    clientDashboardRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      const [resource] = action.clientDashboardCollection;
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        clientDashboardResource: resource,
        clientDashboardCollection: action.clientDashboardCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case CLIENT_DASHBOARD_REFRESH_STARTED: {
      return {
        ...state,
        isClientDashboardRefreshing: true,
        clientDashboardRefreshError: null,
      };
    }
    case CLIENT_DASHBOARD_REFRESH_SUCCEEDED: {
      const [resource] = action.clientDashboardCollection;
      return {
        ...state,
        isClientDashboardRefreshing: false,
        clientDashboardRefreshError: null,
        clientDashboardResource: resource,
        clientDashboardCollection: action.clientDashboardCollection,
      };
    }
    case CLIENT_DASHBOARD_REFRESH_FAILED: {
      return {
        ...state,
        isClientDashboardRefreshing: false,
        clientDashboardRefreshError: action.clientDashboardRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled DashboardContext action type: ${action.type}`);
    }
  }
};

const DashboardContextProvider = ({
  children,
}) => {
  const { clientResource } = useNewClientContext();
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshClientDashboardResource = React.useCallback(async () => {
    try {
      dispatch({ type: CLIENT_DASHBOARD_REFRESH_STARTED });

      const clientDashboardCollection = await clientResource.relationship(fr.relationships.clientDashboard.name).link('related').get();

      dispatch({
        type: CLIENT_DASHBOARD_REFRESH_SUCCEEDED,
        clientDashboardCollection,
      });
    } catch (err) {
      dispatch({ type: CLIENT_DASHBOARD_REFRESH_FAILED, clientRefreshError: err });
    }
  }, [clientResource]);

  const handleNavigateToSurvey = React.useCallback((link) => {
    const url = new URL(window.location.href);

    url.pathname = clientPaths.survey;

    if (link) {
      url.searchParams.set(STEP_LINK_QUERY_PARAM_NAME, link.url());
    } else {
      url.searchParams.delete(STEP_LINK_QUERY_PARAM_NAME);
    }

    history.replace(url.pathname + url.search + url.hash);
  }, []);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      clientDashboardResource: state.clientDashboardResource,
      isClientDashboardRefreshing: state.isClientDashboardRefreshing,
      clientDashboardRefreshError: state.clientDashboardRefreshError,
      refreshClientDashboardResource,
      handleNavigateToSurvey,
    };
  }, [state, refreshClientDashboardResource, handleNavigateToSurvey]);

  useComponentDidMount(async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const clientDashboardCollection = await clientResource
        .relationship(fr.relationships.clientDashboard.name)
        .link('related')
        .get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        clientDashboardCollection,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  });

  return (
    <DashboardContext.Provider value={memoizedValue}>
      {children}
    </DashboardContext.Provider>
  );
};

const useDashboardContext = () => {
  const context = React.useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboardContext must be used within a DashboardContextProvider');
  }
  return context;
};

DashboardContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  DashboardContextProvider,
  useDashboardContext,
};
